import { FormEvent, useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import Input, { InputStyles } from 'components/shared/form/Input';
import Workflow, { WorkflowAction } from 'models/Workflow';
import { WorkflowValuesInput } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

interface Props {
  action: WorkflowAction;
  workflow: Workflow;
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => void;
  isCompleting: boolean;
}

// "Careport" from "careport" — source systems are lowercase identifiers.
function sourceSystemLabel(raw: string): string {
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

// Where to go get the information — the whole instruction the kickoff carried. A URL
// pointer renders as a link (and later becomes the browser-extension hand-off).
function SourceContext({ workflow }: { workflow: Workflow }) {
  const system = workflow.valueFor('source_system')?.value;
  const pointer = workflow.valueFor('retrieval_pointer')?.value;
  if (!system && !pointer) return null;

  return (
    <SourceLine data-testid='retrieve-source-context'>
      {system && <BodySmallBold>{sourceSystemLabel(system)}</BodySmallBold>}
      {pointer &&
        (/^https?:\/\//.test(pointer) ? (
          <PointerLink href={pointer} target='_blank' rel='noreferrer'>
            {pointer}
          </PointerLink>
        ) : (
          <Pointer>{pointer}</Pointer>
        ))}
    </SourceLine>
  );
}

// Phase 0 stand-in for the source-portal pull (OLIO-2593): a person enters the demographics
// and note the retrieval step would fetch. Candidate facilities are resolved upstream (seeded)
// for now. Completing publishes these values, which unblocks the automated disposition.
export default function RetrievePortalForm({ action, workflow, onComplete, isCompleting }: Props) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [note, setNote] = useState('');

  const ready = Boolean(firstName.trim() && lastName.trim() && dob) && !isCompleting;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;
    onComplete(action.id, {
      patient_first_name: firstName.trim(),
      patient_last_name: lastName.trim(),
      patient_dob: dob,
      referral_note: note.trim(),
    });
  }

  return (
    <Form onSubmit={submit} data-testid='retrieve-portal-form'>
      <SourceContext workflow={workflow} />
      <BodySmall>Enter the information pulled from the source portal.</BodySmall>
      <Row>
        <Field>
          <FieldLabel>First name</FieldLabel>
          <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid='retrieve-first-name' />
        </Field>
        <Field>
          <FieldLabel>Last name</FieldLabel>
          <Input value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid='retrieve-last-name' />
        </Field>
      </Row>
      <Field>
        <FieldLabel>Date of birth</FieldLabel>
        <Input type='date' value={dob} onChange={(e) => setDob(e.target.value)} data-testid='retrieve-dob' />
      </Field>
      <Field>
        <FieldLabel>Referral note</FieldLabel>
        <TextArea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid='retrieve-note' />
      </Field>
      <div>
        <Button type='submit' size='small' disabled={!ready} data-testid='retrieve-submit'>
          Save retrieved information
        </Button>
      </div>
    </Form>
  );
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1 1 160px;
`;

const FieldLabel = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 11px;
`;

const TextArea = styled.textarea`
  ${InputStyles}
  resize: vertical;
  font-family: inherit;
`;

const SourceLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid ${colors.black15};
  border-radius: 6px;
  background: ${colors.black05};
`;

const Pointer = styled(BodySmall)`
  color: ${colors.black50};
  word-break: break-all;
`;

const PointerLink = styled.a`
  font-size: 13px;
  color: ${colors.primaryBlue};
  word-break: break-all;
`;
